import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { signOut } from '../store/slices/authSlice'
import { colors } from '../constants/colors'
import Sidebar from '../components/(admin)/Sidebar'
import PageTopBar from '../components/(admin)/PageTopBar'
import deleteIcon from '../assets/images/delete.png'
import editIcon from '../assets/images/edit.png'

const ProjectBuilder = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const handleSignOut = async () => {
    try {
      await dispatch(signOut()).unwrap()
      navigate('/signin')
    } catch (err) {
      console.error('Logout failed:', err)
    }
  }

  // Static project data matching the image
  const [projects] = useState([
    {
      id: 1,
      name: 'Sales Bot',
      status: 'Active',
      createdDate: 'Apr 25, 2024',
      lastTrainedDate: 'May 2, 2023'
    },
    {
      id: 2,
      name: 'Support Bot',
      status: 'Draft',
      createdDate: 'Apr 25, 2024',
      lastTrainedDate: 'May 28, 2023'
    }
  ])

  const handleEdit = (projectId) => {
    console.log('Edit project:', projectId)
    // TODO: Implement edit functionality
  }

  const handleDelete = (projectId) => {
    console.log('Delete project:', projectId)
    // TODO: Implement delete functionality
  }

  const handleAddNew = () => {
    console.log('Add new project')
    // TODO: Implement add new project functionality
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return '#10B981' // green
      case 'Draft':
        return '#9CA3AF' // gray
      case 'Inactive':
        return '#6B7280'
      default:
        return '#6B7280'
    }
  }

  const getStatusBgColor = (status) => {
    switch (status) {
      case 'Active':
        return '#D1FAE5' // light green
      case 'Draft':
        return '#F3F4F6' // light gray
      case 'Inactive':
        return '#F3F4F6'
      default:
        return '#F3F4F6'
    }
  }

  const containerStyle = {
    marginLeft: '280px',
    padding: '40px',
    backgroundColor: colors.background.primary,
    minHeight: '100vh',
    width: 'calc(100vw - 280px)'
  }

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '40px'
  }

  const titleStyle = {
    fontSize: '32px',
    fontWeight: '700',
    color: colors.text.primary,
    margin: 0
  }

  const createButtonStyle = {
    backgroundColor: colors.primary,
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'background-color 0.2s ease'
  }

  const projectsGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '24px',
    marginBottom: '40px'
  }

  const projectCardStyle = {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    position: 'relative'
  }

  const projectTitleStyle = {
    fontSize: '18px',
    fontWeight: '700',
    color: colors.text.primary,
    margin: 0
  }

  const statusBadgeStyle = (status) => ({
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    backgroundColor: getStatusBgColor(status),
    color: getStatusColor(status),
    width: 'fit-content'
  })

  const projectDetailsStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    fontSize: '14px',
    color: colors.text.secondary
  }

  const detailRowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  }

  const actionsStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: 'auto',
    paddingTop: '16px',
    borderTop: `1px solid ${colors.borderVariants.light}`
  }

  const actionButtonStyle = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s ease'
  }

  const emptyStateStyle = {
    textAlign: 'center',
    padding: '60px 20px',
    color: colors.text.secondary,
    fontSize: '16px'
  }

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <div style={containerStyle}>
        {/* Header */}
        <PageTopBar
          title="Project Builder"
          onAiConfig={() => navigate('/ai-configuration')}
          onEmbeddingGuide={() => navigate('/embedding-guide')}
          onSignOut={handleSignOut}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '24px' }}>
          <button 
            style={createButtonStyle}
            onClick={handleAddNew}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = colors.primaryDark
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = colors.primary
            }}
          >
            <span style={{ fontSize: '20px', lineHeight: 1 }}>+</span>
            Add New Project
          </button>
        </div>

        {/* Projects Grid */}
        {projects.length > 0 ? (
          <div style={projectsGridStyle}>
            {projects.map((project) => (
              <div key={project.id} style={projectCardStyle}>
                <div>
                  <h3 style={projectTitleStyle}>{project.name}</h3>
                </div>
                <div>
                  <span style={statusBadgeStyle(project.status)}>
                    {project.status}
                  </span>
                </div>
                <div style={projectDetailsStyle}>
                  <div style={detailRowStyle}>
                    <span>Created {project.createdDate}</span>
                  </div>
                  <div style={detailRowStyle}>
                    <span>Last Trained {project.lastTrainedDate}</span>
                  </div>
                </div>
                <div style={actionsStyle}>
                  <button
                    style={actionButtonStyle}
                    onClick={() => handleEdit(project.id)}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = colors.background.secondary
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent'
                    }}
                    title="Edit"
                  >
                    <img 
                      src={editIcon} 
                      alt="Edit" 
                      style={{ width: '16px', height: '16px' }} 
                    />
                  </button>
                  <button
                    style={actionButtonStyle}
                    onClick={() => handleDelete(project.id)}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = colors.background.secondary
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent'
                    }}
                    title="Delete"
                  >
                    <img 
                      src={deleteIcon} 
                      alt="Delete" 
                      style={{ width: '16px', height: '16px' }} 
                    />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {/* Empty State Message */}
        <div style={emptyStateStyle}>
          No Project Yet. Start by Creating your first chatbot
        </div>
      </div>
    </div>
  )
}

export default ProjectBuilder

